<template>
  <div
    class="keyword-density-row d-flex flex-row align-start"
    :class="{ 'keyword-density-row--nested': nested }"
  >
      <v-checkbox
        v-model="keyword.checked"
        dense
        hide-details
        class="mt-0 pt-0"
        :disabled="readOnly"
      />
    <v-text-field
      v-if="!readOnly"
      :value="draft"
      :hint="hintText"
      :error="duplicate"
      outlined
      dense
      persistent-hint
      @focus="onFocus"
      @blur="onBlur"
      @input="onDraftInput"
      @keydown.enter.prevent="onEnter"
    />
    <div
      v-else
      class="keyword-density-row__label"
    >
      <div class="keyword-density-row__phrase">
        {{ keyword.keyword }}
      </div>
      <div class="caption text--secondary">
        {{ occurrences }} {{ $t('keywords.occurrences') }}
      </div>
    </div>
    <v-btn
      v-if="deletable"
      small
      icon
      color="error"
      class="mt-0"
      :aria-label="$t('delete')"
      @click="$emit('remove')"
    >
      <v-icon small>
        close
      </v-icon>
    </v-btn>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {Keyword} from '@/model/Article';
    import {phraseDuplicatesTracked} from '@/service/keywordInflection';
    import {beginKeywordDensityEdit, endKeywordDensityEdit} from '@/service/keywordDensityEdit';

    export default Vue.extend({
        name: 'KeywordDensityRow',
        props: {
            keyword: {
                type: Object as PropType<Keyword>,
                required: true
            },
            occurrences: {
                type: Number,
                default: 0
            },
            nested: {
                type: Boolean,
                default: false
            },
            main: {
                type: Boolean,
                default: false
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            deletable: {
                type: Boolean,
                default: true
            },
            rowIndex: {
                type: Number,
                default: -1
            },
            trackedKeywords: {
                type: Array as PropType<Keyword[]>,
                default: () => []
            }
        },
        data: function () {
            return {
                draft: this.keyword ? this.keyword.keyword : '',
                focused: false,
            };
        },
        computed: {
            duplicate: function (): boolean {
                if (!this.warnDuplicate) {
                    return false;
                }
                return phraseDuplicatesTracked(this.draft, this.trackedKeywords, this.rowIndex);
            },
            warnDuplicate: function (): boolean {
                const source = this.keyword && this.keyword.source;
                return !source || source === 'custom';
            },
            hintText: function (): string {
                if (this.duplicate) {
                    return String(this.$t('keywords.alreadyInList'));
                }
                return `${this.occurrences} ${this.$t('keywords.occurrences')}`;
            },
        },
        watch: {
            keyword: {
                handler: function (item: Keyword) {
                    if (!item || this.focused) {
                        return;
                    }
                    this.draft = item.keyword || '';
                },
            },
            'keyword.keyword': function (value: string) {
                if (!this.focused) {
                    this.draft = value || '';
                }
            },
        },
        beforeDestroy: function () {
            this.releaseEditLock();
        },
        methods: {
            onFocus: function () {
                if (this.focused) {
                    return;
                }
                this.focused = true;
                beginKeywordDensityEdit();
            },
            onDraftInput: function (value: string) {
                // Local draft only. Writing keyword.keyword here retriggers the
                // article watcher and can freeze the tab.
                this.draft = value;
            },
            onEnter: function () {
                const input = this.$el && (this.$el as HTMLElement).querySelector('input');
                if (input) {
                    input.blur();
                    return;
                }
                this.commitDraft();
            },
            onBlur: function () {
                this.commitDraft();
            },
            commitDraft: function () {
                if (!this.focused && this.keyword.keyword === this.draft) {
                    return;
                }
                this.releaseEditLock();
                if (this.keyword.keyword !== this.draft) {
                    this.keyword.keyword = this.draft;
                }
            },
            releaseEditLock: function () {
                if (!this.focused) {
                    return;
                }
                this.focused = false;
                endKeywordDensityEdit();
            },
        }
    });
</script>

<style scoped lang="sass">
.keyword-density-row
    width: 100%

    ::v-deep .v-text-field
        font-size: 13px

        > .v-input__control > .v-input__slot
            min-height: 32px !important
            align-items: center !important

        .v-text-field__slot
            display: flex
            align-items: center
            min-height: 32px

        input
            font-size: 13px
            line-height: 1.3
            padding-top: 0 !important
            padding-bottom: 0 !important
            margin-top: 0
            height: 20px
            max-height: 20px

        .v-input__append-inner,
        .v-input__prepend-inner
            margin-top: 0 !important
            align-self: center

.keyword-density-row__label
    flex: 1
    min-width: 0
    padding: 2px 8px 0 0

.keyword-density-row__phrase
    font-size: 13px
    line-height: 1.3
    word-break: break-word

.keyword-density-row--nested
    ::v-deep .v-input--checkbox
        transform: scale(0.92)
        transform-origin: top center
</style>
